import React, { useState, useEffect, useContext } from 'react';
import { withTranslation, useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  Input,
  Divider,
  Link,
  InputAdornment,
} from '@material-ui/core';

import { makeStyles } from '@material-ui/core/styles';
import compose from 'ramda/src/compose';
import LocaleKeys from '../locales/keys';
import { useCreateSession } from '../common/hooks/useCreateSession';
import { LoginInfo } from './loginInfo';
import { useFormik } from 'formik';
import { UserSession } from 'ascendon-web-toolkit/utils';
import { postMessage } from '../utils/postMessage';
import showIcon from '../images/icon-show.svg';
import hideIcon from '../images/icon-hide.svg';
import useRetrieveEligibility from '../common/hooks/useRetrieveEligibility';
import { ConfigMessageContext } from '../app/configMessageContext';
import { useHistory } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  container: {
    backgroundColor: '#F7F4F0',
    borderRadius: '10px',
    border: `1px solid ${theme.palette.custom.lightBlack}`,
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  signInButton: {
    lineHeight: 1,
    textDecoration: 'none',
    textTransform: 'uppercase',
    fontFamily: 'TitilliumWeb-Regular',
    fontSize: '13px',
    letterSpacing: '0.5px',
    fontWeight: 500,
    borderRadius: '5px',
    padding: '10px 30px',
    '&:hover': {
      boxShadow: `0 0 0 2px ${theme.palette.primary.dark} inset`,
      color: theme.palette.custom.black,
      backgroundColor: '#fff',
    },
  },
  signInText: {
    textTransform: 'uppercase',
    fontSize: '32px',
    fontWeight: 500,
    [theme.breakpoints.down('xs')]: {
      fontSize: '22px',
    },
  },
  heading: {
    fontFamily: 'TitilliumWeb-Regular',
    fontSize: '17px',
    letterSpacing: '.2px',
    fontWeight: 400,
    padding: '0 10px',
    marginBottom: '5px',
  },
  input: {
    fontFamily: 'TitilliumWeb-Regular',
    fontSize: '15px',
    border: `1px solid ${theme.palette.custom.lightBlack}`,
    padding: '15px 10px',
    '& ::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 1,
    },
    '& .MuiInputBase-input': {
      marginLeft: 0,
    },
  },
  passwordVisibilityLink: {
    borderStyle: 'none',
    marginRight: '12px',
    cursor: 'default',
  },
  dividerBlack: {
    backgroundColor: `#d0d0d2`,
    height: '1px',
    marginBottom: '30px',
    [theme.breakpoints.down('xs')]: {
      fontSize: '15px',
    },
  },
  forgetPassword: {
    justifyContent: 'end',
    marginRight: '0px',
  },
  secondaryActions: {
    display: 'Flex',
    flexDirection: 'column',
    width: 'fit-content',
    marginTop: '30px',
    marginBottom: '10px',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'row',
      marginTop: '10px',
    },
  },
  doNotHaveAnAccount: {
    marginRight: '15px',
  },
  link: {
    color: 'black',
    textDecoration: `underline solid ${theme.palette.primary.light} 1px`,
    textUnderlineOffset: '3px',
    '&:hover': {
      cursor: 'pointer',
      color: theme.palette.primary.light,
      textDecoration: `underline solid ${theme.palette.primary.light} 1px`,
      textUnderlineOffset: '3px',
    },
  },
  section: {
    [theme.breakpoints.down('xs')]: {
      margin: '30px 20px',
    },
  },
  formSection: {
    marginBottom: '30px',
  },
  errorTypography: {
    fontFamily: 'Formula1-Regular',
  },
  actionTypoGraphy: {
    fontFamily: 'TitilliumWeb-Regular',
    fontSize: '17px',
    letterSpacing: '.2px',
  },
}));

function SignIn(props) {
  const { staticApiKey, staticApiUrl, language } = props;
  const { isQuickCancel, setIsQuickCancel } = useContext(ConfigMessageContext);
  const classes = useStyles();
  const { t } = useTranslation();
  const [errors, setErrors] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [createSessionResponse, createSessionFault, requestCreateSession] =
    useCreateSession();
  const [
    retrieveEligibilityUiModel,
    retrieveEligibilityFault,
    requestRetrieveEligibility,
  ] = useRetrieveEligibility();
  const history = useHistory();
  const requestFaults = [createSessionFault];
  const LoginFormModel = new LoginInfo();

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const form = useFormik({
    initialValues: LoginFormModel.initialFormValues,
    validate: (values) => LoginFormModel.validate(values),
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: (values) => {
      // Toggle global loader ON
      postMessage('module.showLoader', {});
      const { emailAddress, password } = values;
      const body = {
        Login: emailAddress,
        Password: password,
      };
      requestCreateSession(body);
    },
  });

  const navigateToMyAccount = () => {
    setTimeout(function () {
      setIsQuickCancel(false);
      setTimeout(function () {
        history.push('/my-account');
      }, 500);
    }, 500);
  };

  const navigateToMySubscription = (quickCancelEnabled) => {
    setIsQuickCancel(!!quickCancelEnabled);
    setTimeout(function () {
      history.push('/my-subscription');
    }, 500);
  };

  useEffect(() => {
    if (retrieveEligibilityUiModel) {
      if (retrieveEligibilityUiModel?.EntitledPlans?.length) {
        if (
          retrieveEligibilityUiModel?.EntitledPlans?.length === 1 &&
          retrieveEligibilityUiModel?.EntitledPlans?.[0]?.appSource === 'Web'
        ) {
          navigateToMySubscription(!!isQuickCancel);
        } else {
          navigateToMySubscription(false);
        }
      } else {
        navigateToMyAccount();
      }
    }
  }, [retrieveEligibilityUiModel]);

  useEffect(() => {
    if (retrieveEligibilityFault) {
      navigateToMyAccount();
    }
  }, [retrieveEligibilityFault]);

  useEffect(() => {
    if (createSessionResponse) {
      // Toggle global loader OFF
      postMessage('module.hideLoader', {});
      UserSession.setSessionData(createSessionResponse);
      postMessage('subscriber.loggedIn', createSessionResponse);
      requestRetrieveEligibility({ staticApiKey, staticApiUrl, language });
    }
  }, [createSessionResponse]);

  useEffect(() => {
    const filterFaults = requestFaults.filter((fault) => !!fault);
    if (filterFaults.length) {
      // Toggle global loader OFF
      postMessage('module.hideLoader', {});
      const currentFault = filterFaults[0];
      setErrors([currentFault]);
    }
  }, [...requestFaults]);

  useEffect(() => {
    // Toggle global loader OFF
    postMessage('module.hideLoader', {});
  }, []);

  return (
    <Container className={classes.container} width={733}>
      <Box mt={10} mb={10} ml={20} mr={20} className={classes.section}>
        <Box mb={5}>
          <Typography variant="h2" className={classes.signInText}>
            {t(LocaleKeys.signIn.signIn)}
          </Typography>
        </Box>

        <Divider className={classes.dividerBlack} />

        <form>
          <Box>
            <Box className={classes.formSection}>
              <Typography className={classes.heading}>
                {t(LocaleKeys.fields.emailAddress)}
              </Typography>
              <Input
                className={classes.input}
                disableUnderline
                fullWidth
                name="emailAddress"
                onChange={(event) => {
                  form.setFieldValue('emailAddress', event.target.value);
                }}
                placeholder={t(LocaleKeys.signIn.EnterYourUsername)}
              />
              <Box ml={3}>
                <Typography
                  variant="body2"
                  color="error"
                  className={classes.errorTypography}
                >
                  {form.errors.emailAddress}
                </Typography>
              </Box>
            </Box>
            <Box className={classes.formSection}>
              <Typography className={classes.heading}>
                {t(LocaleKeys.fields.password)}
              </Typography>
              <Input
                className={classes.input}
                disableUnderline
                fullWidth
                type={showPassword ? 'text' : 'password'}
                name="password"
                onChange={(event) => {
                  form.setFieldValue('password', event.target.value);
                }}
                placeholder={t(LocaleKeys.signIn.EnterYourPassword)}
                endAdornment={
                  <InputAdornment position="end">
                    <Link
                      className={classes.passwordVisibilityLink}
                      underline="none"
                      data-testid="toggle-password-visibility"
                      onClick={togglePasswordVisibility}
                    >
                      <img
                        src={showPassword ? showIcon : hideIcon}
                        alt={
                          showPassword
                            ? t(LocaleKeys.registration.hidePassword)
                            : t(LocaleKeys.registration.showPassword)
                        }
                      />
                    </Link>
                  </InputAdornment>
                }
              />
              <Box ml={3}>
                <Typography
                  variant="body2"
                  color="error"
                  className={classes.errorTypography}
                >
                  {form.errors.password}
                </Typography>
              </Box>
            </Box>
            <Box component={Grid} container display="flex" justifyContent="end">
              <Grid item className={classes.forgetPassword}>
                <Link
                  className={classes.link}
                  onClick={() => {
                    postMessage('module.loginButton', {});
                    alert('Handled By Digitas');
                  }}
                >
                  <Typography variant="h6" className={classes.actionTypoGraphy}>
                    {t(LocaleKeys.signIn.forgotPassword)}
                  </Typography>
                </Link>
              </Grid>
            </Box>
            <Box component={Grid} container style={{ marginTop: '15px' }}>
              <Grid container justifyContent="space-between">
                <Button
                  className={classes.signInButton}
                  variant="contained"
                  color="primary"
                  disableElevation
                  onClick={(e) => {
                    e.preventDefault();
                    form.handleSubmit();
                  }}
                >
                  {t(LocaleKeys.signIn.signIn)}
                </Button>
              </Grid>

              <Grid container className={classes.secondaryActions}>
                <Grid item xs={12} className={classes.doNotHaveAnAccount}>
                  <Typography
                    variant="h6"
                    className={classes.actionTypoGraphy}
                    style={{ fontWeight: '400' }}
                  >
                    {t(LocaleKeys.signIn.doNotHaveAnAccountYet)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Link
                    to="/registration"
                    className={classes.link}
                    onClick={() => {
                      postMessage('module.loginButton', {});
                    }}
                  >
                    <Typography
                      variant="h6"
                      className={classes.actionTypoGraphy}
                    >
                      <b>{t(LocaleKeys.signIn.registerWithF1)}</b>
                    </Typography>
                  </Link>
                </Grid>
              </Grid>
            </Box>
          </Box>
          {errors && errors.length ? (
            <Box mt={10}>
              <Typography variant="body2" color="error">
                {errors[0]}
              </Typography>
            </Box>
          ) : null}
        </form>
      </Box>
    </Container>
  );
}

export default compose(withTranslation())(SignIn);
